import React, { useMemo } from 'react';
import Editor from '@monaco-editor/react';
import { useTranslation } from 'react-i18next';
import { MONACO_OPTIONS } from 'routes/schema-builder/editorConfig';
import { FieldMapping, isStreamType, Rule } from './fieldMappingTypes';

type FieldMappingCodeEditorProps = {
  value: string;
  onChange: (value: string) => void;
  providerId: string;
  destinationId: string;
  onValidChange: (parsed: Rule | null) => void;
};

export type CodeValidationResult =
  ({ ok: true } & Rule) | { ok: false; error: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function validateCode(
  raw: string,
  providerId: string,
  destinationId: string,
  t: (key: string) => string,
): CodeValidationResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, error: t('Invalid JSON') };
  }
  if (!isRecord(parsed) || !isRecord(parsed.field_mappings)) {
    return { ok: false, error: t('Invalid JSON') };
  }
  const providers = Object.keys(parsed.field_mappings);
  if (providers.length !== 1 || providers[0] !== providerId) {
    return { ok: false, error: t('Provider ID does not match this Route') };
  }
  const providerEntry = parsed.field_mappings[providerId];
  if (!isRecord(providerEntry)) {
    return { ok: false, error: t('Invalid JSON') };
  }
  const streamTypes = Object.keys(providerEntry);
  const candidateStream = streamTypes[0];
  if (streamTypes.length !== 1 || !isStreamType(candidateStream)) {
    return { ok: false, error: t('Invalid JSON') };
  }
  const streamEntry = providerEntry[candidateStream];
  if (!isRecord(streamEntry)) {
    return { ok: false, error: t('Invalid JSON') };
  }
  const destinations = Object.keys(streamEntry);
  if (destinations.length !== 1 || destinations[0] !== destinationId) {
    return {
      ok: false,
      error: t('Destination ID does not match this destination'),
    };
  }
  const mappingCandidate = streamEntry[destinationId];
  if (
    !isRecord(mappingCandidate) ||
    typeof mappingCandidate.destination_field !== 'string'
  ) {
    return { ok: false, error: t('Invalid JSON') };
  }
  // sanitizeMapping handles loose value typing at the persistence boundary; here we
  // just hand off the validated shape to the caller.
  const mapping = mappingCandidate as unknown as FieldMapping;
  return { ok: true, streamType: candidateStream, mapping };
}

const FieldMappingCodeEditor = ({
  value,
  onChange,
  providerId,
  destinationId,
  onValidChange,
}: FieldMappingCodeEditorProps) => {
  const { t } = useTranslation();
  const validation = useMemo(
    () => validateCode(value, providerId, destinationId, t),
    [value, providerId, destinationId, t],
  );

  React.useEffect(() => {
    onValidChange(
      validation.ok
        ? { streamType: validation.streamType, mapping: validation.mapping }
        : null,
    );
  }, [validation, onValidChange]);

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="FieldMappingCodeEditor-container"
    >
      <div
        className="overflow-hidden rounded border border-ui-divider-lines"
        style={{ height: 280 }}
      >
        <Editor
          height="100%"
          language="json"
          theme="vs"
          value={value}
          options={MONACO_OPTIONS(false)}
          onChange={(nextValue) => onChange(nextValue ?? '')}
        />
      </div>
      {!validation.ok && (
        <p
          className="text-body-sm text-ui-status-error-destructive"
          data-testid="FieldMappingCodeEditor-error"
          role="alert"
        >
          {validation.error}
        </p>
      )}
    </div>
  );
};

export default FieldMappingCodeEditor;
